<template>
  <div class="letters-wrap">
    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">← بازگشت</button>
      <h1 class="title">بازی حروف 🔤</h1>
      <div class="diff-pills">
        <button 
          v-for="lvl in ['easy', 'medium', 'hard']" 
          :key="lvl" 
          class="diff-pill" 
          :class="{ active: level === lvl }"
          @click="setLevel(lvl)"
        >
          {{ getLevelLabel(lvl) }}
        </button>
      </div>
    </div>

    <!-- Letter Selection (Scrollable Rail) -->
    <div class="letters-rail-wrap">
      <div class="letters-rail">
        <button 
          v-for="ch in letters" 
          :key="ch" 
          class="letter-key" 
          :class="{ active: ch === letter }" 
          @click="selectLetter(ch)"
        >
          {{ ch }}
        </button>
      </div>
    </div>

    <!-- Game Area -->
    <div class="game-area">
      <div class="controls-row">
        <div class="stats">
          <div class="stat-box">
            <span class="stat-icon">⏱️</span>
            <span class="stat-value">{{ timeText }}</span>
          </div>
          <div class="stat-box">
            <span class="stat-icon">✏️</span>
            <span class="stat-value">{{ strokes }} قلم</span>
          </div>
        </div>
        <div class="actions-row">
          <button class="action-btn start" v-if="!started" @click="startGame">شروع 🏁</button>
          <button class="action-btn finish" v-else @click="finishGame">بررسی ✅</button>
          <button class="action-btn clear" @click="clearTrace" :disabled="!started">پاک‌کن 🧹</button>
        </div>
      </div>

      <div class="board-container">
        <div class="board" :style="{ width: boardW + 'px', height: boardH + 'px' }">
          <!-- Reference Layer (Gray Letter) -->
          <canvas ref="refRef" class="canvas-layer reference" :width="boardW" :height="boardH"></canvas>
          
          <!-- Tracing Layer (Blue Ink) -->
          <canvas 
            ref="traceRef" 
            class="canvas-layer trace" 
            :width="boardW" 
            :height="boardH" 
            @pointerdown="onDown" 
            @pointermove="onMove" 
            @pointerup="onUp" 
            @pointercancel="onUp" 
            @pointerleave="onUp"
          ></canvas>
          
          <div v-if="cheerOn" class="cheer-bubble">{{ cheerMsg }}</div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-card">
        <button class="close-btn" @click="showModal=false">✕</button>
        <div class="confetti">🎉</div>
        <h2>آفرین! عالی نوشتی! 🌟</h2>
        <p class="modal-stats">حرف: <strong>{{ letter }}</strong> • زمان: {{ timeText }} • قلم‌ها: {{ strokes }}</p>
        <div class="modal-actions">
          <button class="action-btn magic" @click="startMagicMode" :disabled="isLoadingExample">
            {{ isLoadingExample ? 'در حال بارگذاری...' : 'جادو ✨' }}
          </button>
          <button class="action-btn primary" @click="nextLetter">حرف بعدی ⏭️</button>
          <button class="action-btn secondary" @click="startGame">دوباره 🔄</button>
          <button class="action-btn outline" @click="goBack">خروج 🏠</button>
        </div>
      </div>
    </div>

    <!-- Magic Flow Modal -->
    <div v-if="showMagicModal" class="modal-overlay">
      <div class="modal-content magic-theme">
        <button class="close-btn" @click="closeMagicModal" v-if="!isGeneratingImage">✕</button>
        
        <!-- 1. Intro & Word Display -->
        <div v-if="exampleWord && currentQuestionIndex === 0" class="magic-step">
          <div class="magic-icon">✨</div>
          <h2 class="magic-title">مثال برای حرف {{ letter }}</h2>
          <div class="example-word-display">{{ exampleWord }}</div>
          <p class="magic-subtitle">حالا بیا دربارهٔ {{ exampleWord }} چندتا سوال بپرسم تا تصویرشو بسازیم!</p>
          <button class="action-btn magic-start" @click="currentQuestionIndex = 1">بزن بریم! 🚀</button>
        </div>

        <!-- 2. Questions -->
        <div v-else-if="currentQuestionIndex > 0 && currentQuestionIndex <= magicQuestions.length" class="magic-step">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: ((currentQuestionIndex - 1) / magicQuestions.length * 100) + '%' }"></div>
          </div>
          <h3 class="question-text">{{ magicQuestions[currentQuestionIndex - 1] }}</h3>
          <input 
            v-model="userAnswer" 
            type="text" 
            class="magic-input" 
            placeholder="اینجا بنویس..." 
            @keyup.enter="submitAnswer"
          />
          <button class="action-btn magic-submit" @click="submitAnswer" :disabled="!userAnswer.trim()">
            {{ currentQuestionIndex < magicQuestions.length ? 'سوال بعدی ⏭' : 'ساخت تصویر! 🎨' }}
          </button>
        </div>

        <!-- 3. Generating State (Loading) -->
        <div v-else-if="isGeneratingImage" class="magic-loading">
          <div class="magic-spinner">
            <div class="wand">🪄</div>
            <div class="sparkles">✨</div>
          </div>
          <h3>در حال انجام جادو... 🧞‍♂️</h3>
          <p>هوش مصنوعی داره تصویر {{ exampleWord }} رو با جواب‌های تو می‌سازه!</p>
          <div class="progress-bar-magic"><div class="fill"></div></div>
        </div>

        <!-- 4. Result -->
        <div v-else-if="generatedImageUrl" class="magic-step">
          <h2>تصویر جادویی آماده شد! 🎉</h2>
          <div class="image-wrapper">
             <img :src="generatedImageUrl" alt="Generated image" class="generated-image" />
          </div>
          <p class="magic-result-text">اینه! {{ exampleWord }} با مشخصاتی که گفتی.</p>
          <button class="action-btn primary" @click="closeMagicModal">عالی بود! بستن</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useMutation, useApolloClient } from '@vue/apollo-composable'
import { useRoute, useRouter } from 'vue-router'
import { 
  SUBMIT_LETTERS_MUTATION, 
  GET_LETTER_EXAMPLE_MUTATION, 
  ANSWER_LETTER_QUESTION_MUTATION, 
  GENERATE_LETTER_IMAGE_MUTATION 
} from '../api/mutations'
import { CHECK_LETTER_MAGIC_STATUS_QUERY } from '../api/queries'

const router = useRouter()
const route = useRoute()
const slug = route.params.slug
const { client } = useApolloClient()

// Constants
const letters = ['ا','ب','پ','ت','ث','ج','چ','ح','خ','د','ذ','ر','ز','ژ','س','ش','ص','ض','ط','ظ','ع','غ','ف','ق','ک','گ','ل','م','ن','و','ه','ی']
const cheerList = ['آفرین! 👏','عالی بود! 🌟','دمت گرم! 💪','چه عالی! 🎉','احسنت! 🥳']

// Game State
const refRef = ref(null)
const traceRef = ref(null)
const boardW = ref(320)
const boardH = ref(320)
const level = ref('easy')
const letter = ref('ا')
const strokes = ref(0)
const success = ref(false)
const started = ref(false)
const secs = ref(0)
const cheerOn = ref(false)
const cheerMsg = ref('')
const showModal = ref(false)
const drawing = reactive({ on: false, x: 0, y: 0 })

// Magic State
const magicMode = ref(false)
const magicSessionId = ref(null)
const exampleWord = ref('')
const magicQuestions = ref([])
const currentQuestionIndex = ref(0)
const showMagicModal = ref(false)
const generatedImageUrl = ref(null)
const isLoadingExample = ref(false)
const isGeneratingImage = ref(false)
const userAnswer = ref('')

let timerInterval
let cheerTimer
let pollInterval

const { mutate: submitLetters } = useMutation(SUBMIT_LETTERS_MUTATION)
const { mutate: getLetterExample } = useMutation(GET_LETTER_EXAMPLE_MUTATION)
const { mutate: answerLetterQuestion } = useMutation(ANSWER_LETTER_QUESTION_MUTATION)
const { mutate: generateLetterImage } = useMutation(GENERATE_LETTER_IMAGE_MUTATION)

// --- COMPUTED ---
const timeText = computed(() => {
  const m = Math.floor(secs.value / 60).toString().padStart(2, '0')
  const s = Math.floor(secs.value % 60).toString().padStart(2, '0')
  return `${m}:${s}`
})

// --- GAME LOGIC ---
function goBack() { router.push(`/room/${slug}`) }
function getLevelLabel(l) { return { 'easy': 'آسان', 'medium': 'متوسط', 'hard': 'سخت' }[l] }
function setLevel(v) { level.value = v; drawReference() }

function selectLetter(ch) {
  letter.value = ch
  started.value = false
  stopTimer()
  secs.value = 0
  strokes.value = 0
  clearTrace()
  drawReference()
}

function nextLetter() {
  const idx = letters.indexOf(letter.value)
  selectLetter(idx < letters.length - 1 ? letters[idx + 1] : letters[0])
  showModal.value = false
}

function startTimer() {
  stopTimer()
  secs.value = 0
  timerInterval = setInterval(() => { secs.value++ }, 1000)
}

function stopTimer() { clearInterval(timerInterval) }

function drawReference() {
  const el = refRef.value
  if (!el) return
  const ctx = el.getContext('2d')
  ctx.clearRect(0, 0, boardW.value, boardH.value)
  const size = Math.min(boardW.value, boardH.value) * 0.6
  ctx.fillStyle = '#e5e7eb'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `bold ${size}px "Vazirmatn", "Tahoma", sans-serif`
  ctx.fillText(letter.value, boardW.value/2, boardH.value/2 + size*0.1)
}

function clearTrace() {
  const el = traceRef.value
  if (!el) return
  const ctx = el.getContext('2d')
  ctx.clearRect(0, 0, boardW.value, boardH.value)
}

function startGame() {
  strokes.value = 0
  success.value = false
  started.value = true
  showModal.value = false
  clearTrace()
  drawReference()
  startTimer()
}

function finishGame() {
  if (started.value) {
    started.value = false
    stopTimer()
    checkCompletion(true)
  }
}

// Canvas Interaction
function onDown(e) {
  if (!started.value) return
  e.preventDefault()
  const rect = traceRef.value.getBoundingClientRect()
  const cx = e.clientX || e.touches?.[0]?.clientX
  const cy = e.clientY || e.touches?.[0]?.clientY
  drawing.on = true
  drawing.x = cx - rect.left
  drawing.y = cy - rect.top
  strokes.value++
  if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId)
}

function onMove(e) {
  if (!started.value || !drawing.on) return
  e.preventDefault()
  const rect = traceRef.value.getBoundingClientRect()
  const cx = e.clientX || e.touches?.[0]?.clientX
  const cy = e.clientY || e.touches?.[0]?.clientY
  const x = cx - rect.left
  const y = cy - rect.top
  
  const ctx = traceRef.value.getContext('2d')
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#2563eb'
  ctx.lineWidth = level.value === 'easy' ? 40 : (level.value === 'medium' ? 30 : 20)
  
  ctx.beginPath()
  ctx.moveTo(drawing.x, drawing.y)
  ctx.lineTo(x, y)
  ctx.stroke()
  drawing.x = x
  drawing.y = y
}

function onUp() {
  if (started.value) {
    drawing.on = false
    if (Math.random() > 0.7) showCheer()
    checkCompletion(false)
  }
}

function showCheer() {
  clearTimeout(cheerTimer)
  cheerMsg.value = cheerList[Math.floor(Math.random() * cheerList.length)]
  cheerOn.value = true
  cheerTimer = setTimeout(() => cheerOn.value = false, 1500)
}

function checkCompletion(force) {
  if (!refRef.value || !traceRef.value) return
  const w = boardW.value
  const h = boardH.value
  const rData = refRef.value.getContext('2d').getImageData(0, 0, w, h).data
  const tData = traceRef.value.getContext('2d').getImageData(0, 0, w, h).data
  
  let refP = 0
  let ovP = 0
  
  for (let i = 3; i < rData.length; i += 16) {
    if (rData[i] > 20) {
      refP++
      if (tData[i] > 20) ovP++
    }
  }
  
  if (refP === 0) return
  
  const cov = ovP / refP
  const th = level.value === 'easy' ? 0.6 : (level.value === 'medium' ? 0.75 : 0.85)
  
  if (cov >= th) {
    success.value = true
    started.value = false
    stopTimer()
    showModal.value = true
    submitResult()
  }
}

async function submitResult() {
  try {
    await submitLetters({
      data: {
        roomSlug: slug,
        letter: letter.value,
        referenceBase64: refRef.value.toDataURL(),
        traceBase64: traceRef.value.toDataURL(),
        durationSeconds: secs.value,
        strokes: strokes.value,
        level: level.value,
        declaredDone: true,
        success: true
      }
    })
  } catch (e) { console.error(e) }
}

// --- MAGIC FEATURE (Async Polling Flow) ---

async function startMagicMode() {
  isLoadingExample.value = true
  try {
    const result = await getLetterExample({ data: { letter: letter.value, childName: 'کودک', roomSlug: slug } })
    if (result?.data?.getLetterExample) {
      const { exampleWord: word, questions, sessionId } = result.data.getLetterExample
      exampleWord.value = word
      magicQuestions.value = questions
      magicSessionId.value = sessionId
      currentQuestionIndex.value = 0
      showMagicModal.value = true
      showModal.value = false
    }
  } catch (e) {
    console.error('Magic start error:', e)
  } finally {
    isLoadingExample.value = false
  }
}

async function submitAnswer() {
  if (!userAnswer.value.trim()) return
  try {
    const result = await answerLetterQuestion({
      data: {
        sessionId: magicSessionId.value,
        questionIndex: currentQuestionIndex.value - 1,
        answer: userAnswer.value
      }
    })
    userAnswer.value = ''
    if (result?.data?.answerLetterQuestion?.completed) {
      await finalizeImage()
    } else {
      currentQuestionIndex.value++
    }
  } catch (e) { console.error(e) }
}

async function finalizeImage() {
  isGeneratingImage.value = true
  try {
    // 1. Trigger Async Generation
    const result = await generateLetterImage({
      data: { sessionId: magicSessionId.value, roomSlug: slug }
    })
    
    if (result?.data?.generateLetterImage?.ok) {
      // 2. Start Polling for results
      pollInterval = setInterval(checkStatus, 3000)
    } else {
      isGeneratingImage.value = false
      alert('خطا در شروع ساخت تصویر')
    }
  } catch (e) {
    console.error(e)
    isGeneratingImage.value = false
  }
}

async function checkStatus() {
  try {
    const { data } = await client.query({
      query: CHECK_LETTER_MAGIC_STATUS_QUERY,
      variables: { sessionId: magicSessionId.value },
      fetchPolicy: 'network-only'
    })
    
    const status = data?.checkLetterMagicStatus?.imageStatus
    const url = data?.checkLetterMagicStatus?.imageUrl

    if (status === 'completed' && url) {
      clearInterval(pollInterval)
      generatedImageUrl.value = url
      isGeneratingImage.value = false
    } else if (status === 'failed') {
      clearInterval(pollInterval)
      isGeneratingImage.value = false
      alert('متاسفانه ساخت تصویر با خطا مواجه شد.')
    }
  } catch (e) {
    console.error("Polling error", e)
  }
}

function closeMagicModal() {
  showMagicModal.value = false
  magicMode.value = false
  exampleWord.value = ''
  magicQuestions.value = []
  currentQuestionIndex.value = 0
  generatedImageUrl.value = null
  isGeneratingImage.value = false
  if (pollInterval) clearInterval(pollInterval)
}

onMounted(() => {
  const updateSize = () => {
    const w = Math.min(window.innerWidth - 32, 500)
    const h = Math.min(window.innerHeight - 300, 500)
    const size = Math.max(280, Math.min(w, h))
    boardW.value = size; boardH.value = size
    setTimeout(drawReference, 50)
  }
  window.addEventListener('resize', updateSize)
  updateSize()
})

onUnmounted(() => {
  stopTimer()
  if (pollInterval) clearInterval(pollInterval)
})
</script>

<script>
export default { name: 'LettersGame' }
</script>

<style scoped>
/* Main Layout */
.letters-wrap { min-height: 100vh; padding: 16px; background: linear-gradient(135deg, #fce7f3 0%, #dbeafe 50%, #ecfdf5 100%); font-family: 'Vazirmatn', sans-serif; display: flex; flex-direction: column; }

/* Header */
.header { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 20px; }
.back-btn { background: linear-gradient(135deg, #fff 0%, #fef2f2 100%); border: 3px solid #f87171; border-radius: 999px; padding: 8px 16px; font-weight: 700; color: #b91c1c; cursor: pointer; transition: all 0.2s; }
.back-btn:hover { transform: scale(1.05); }
.title { flex: 1; font-size: 24px; margin: 0; text-align: center; background: linear-gradient(135deg, #db2777 0%, #7c3aed 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }

/* Difficulty */
.diff-pills { display: flex; gap: 6px; justify-content: center; }
.diff-pill { border: none; background: #f3f4f6; padding: 6px 12px; border-radius: 999px; color: #4b5563; font-weight: 600; cursor: pointer; font-size: 13px; transition: all 0.2s; }
.diff-pill.active { background: #8b5cf6; color: #fff; box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3); }

/* Rail */
.letters-rail-wrap { width: 100%; overflow-x: auto; padding-bottom: 10px; margin-bottom: 16px; }
.letters-rail { display: flex; gap: 8px; padding: 4px; width: max-content; }
.letter-key { width: 48px; height: 48px; border-radius: 12px; border: 2px solid #e5e7eb; background: #fff; font-size: 20px; font-weight: bold; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; color: #374151; }
.letter-key.active { border-color: #3b82f6; background: #eff6ff; color: #2563eb; transform: scale(1.1); box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2); }

/* Game Area */
.game-area { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 16px; }
.controls-row { width: 100%; max-width: 500px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
.stats { display: flex; gap: 10px; }
.stat-box { background: #fff; padding: 6px 12px; border-radius: 12px; display: flex; align-items: center; gap: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); font-size: 14px; color: #4b5563; }
.stat-value { font-weight: bold; color: #111; }
.actions-row { display: flex; gap: 8px; }
.action-btn { border: none; border-radius: 8px; padding: 8px 16px; font-weight: 700; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.action-btn.start { background: #22c55e; color: #fff; box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3); }
.action-btn.finish { background: #3b82f6; color: #fff; }
.action-btn.clear { background: #f3f4f6; color: #4b5563; }
.action-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Canvas */
.board-container { position: relative; padding: 4px; background: linear-gradient(135deg, #f472b6, #a78bfa, #22d3ee); border-radius: 24px; box-shadow: 0 12px 32px rgba(0,0,0,0.15); }
.board { background: #fff; border-radius: 20px; position: relative; overflow: hidden; touch-action: none; }
.canvas-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.reference { z-index: 1; }
.trace { z-index: 2; cursor: crosshair; }
.cheer-bubble { position: absolute; bottom: 20px; right: 20px; background: #fff; padding: 8px 16px; border-radius: 16px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); font-weight: bold; color: #db2777; z-index: 10; animation: pop-in 0.3s ease; }
@keyframes pop-in { from { transform: scale(0); } to { transform: scale(1); } }

/* Basic Modals */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.modal-card { background: #fff; border-radius: 24px; padding: 30px; text-align: center; max-width: 400px; width: 100%; box-shadow: 0 20px 40px rgba(0,0,0,0.2); position: relative; animation: slide-up 0.4s ease; }
@keyframes slide-up { from { transform: translateY(50px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.close-btn { position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border-radius: 50%; border: none; background: #f3f4f6; cursor: pointer; color: #6b7280; font-weight: bold; }
.confetti { font-size: 60px; margin-bottom: 10px; }
.modal-stats { color: #6b7280; font-size: 16px; margin: 10px 0 24px; }
.modal-actions { display: flex; flex-direction: column; gap: 10px; }
.action-btn.primary { background: #8b5cf6; color: #fff; padding: 12px; font-size: 16px; }
.action-btn.secondary { background: #10b981; color: #fff; padding: 12px; font-size: 16px; }
.action-btn.outline { background: transparent; border: 2px solid #e5e7eb; color: #4b5563; padding: 10px; }

/* Magic Feature Styles */
.modal-content.magic-theme { 
  background: linear-gradient(135deg, #fff 0%, #f0f9ff 100%); 
  border: 4px solid #bae6fd; 
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); 
  border-radius: 24px; 
  padding: 32px; 
  text-align: center; 
  width: 90%; 
  max-width: 480px; 
  position: relative; 
  min-height: 400px; 
  display: flex; 
  flex-direction: column; 
  justify-content: center;
}
.action-btn.magic { background: linear-gradient(135deg, #f59e0b 0%, #ec4899 100%); color: #fff; padding: 12px; font-size: 16px; font-weight: 700; box-shadow: 0 4px 16px rgba(245, 158, 11, 0.4); }
.magic-icon { font-size: 64px; margin-bottom: 16px; animation: sparkle 1.5s infinite; }
@keyframes sparkle { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.2); opacity: 0.8; } }
.magic-title { font-size: 24px; margin-bottom: 16px; color: #7c3aed; }
.example-word-display { font-size: 48px; font-weight: bold; color: #ec4899; margin: 20px 0; padding: 20px; background: linear-gradient(135deg, #fef3c7, #fce7f3); border-radius: 16px; }
.magic-subtitle { font-size: 18px; color: #6b7280; margin-bottom: 24px; }
.action-btn.magic-start { background: linear-gradient(135deg, #8b5cf6, #ec4899); color: #fff; padding: 14px 28px; font-size: 18px; }
.progress-bar { width: 100%; height: 8px; background: #e5e7eb; border-radius: 4px; margin-bottom: 24px; overflow: hidden; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #8b5cf6, #ec4899); transition: width 0.3s ease; }
.question-text { font-size: 22px; color: #374151; margin-bottom: 20px; }
.magic-input { width: 100%; padding: 14px; font-size: 18px; border: 2px solid #e5e7eb; border-radius: 12px; margin-bottom: 16px; font-family: inherit; box-sizing: border-box; }
.magic-input:focus { outline: none; border-color: #8b5cf6; }
.action-btn.magic-submit { background: linear-gradient(135deg, #10b981, #06b6d4); color: #fff; padding: 12px 24px; font-size: 16px; width: 100%; }

/* Magic Loading & Result */
.magic-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; text-align: center; padding: 20px; }
.magic-spinner { font-size: 60px; margin-bottom: 20px; position: relative; animation: float 3s ease-in-out infinite; }
.wand { position: relative; z-index: 2; animation: wave 1.5s ease-in-out infinite; }
.sparkles { position: absolute; top: -10px; right: -20px; font-size: 40px; animation: sparkle 1s linear infinite; }
.progress-bar-magic { width: 100%; height: 8px; background: #e0f2fe; border-radius: 99px; overflow: hidden; margin-top: 20px; }
.progress-bar-magic .fill { height: 100%; width: 50%; background: linear-gradient(90deg, #8b5cf6, #ec4899); animation: progress-indeterminate 2s infinite linear; }
@keyframes wave { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(-20deg); } }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes progress-indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
.generated-image { max-width: 100%; border-radius: 16px; margin: 20px 0; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15); max-height: 250px; object-fit: contain; }
.magic-result-text { font-size: 18px; color: #4b5563; margin-bottom: 20px; }
</style>